import {
    createAssetText,
    showAllAssetsText,
    showLessAssetsText,
} from './getCreateAssetMenuItems.locstring.json';
import type { CreateAssetMenuItemsConfig } from '../schema/CreateAssetMenuItemsConfig';
import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
// tslint:disable-next-line:no-duplicate-imports
import { ContextualMenuItemType } from '@fluentui/react/lib/ContextualMenu';
import loc from 'owa-localize';
import type { AssetDescriptor } from 'owa-timestream-asset-sdk-types';
import { lazyGetTimeStreamAssetMenuItem, splitDisplayedItems } from 'owa-timestream-canvas';
import { FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import type * as React from 'react';
import { CalendarIcons } from 'owa-calendar-icons';

/**
 * Unique identifier allowing other components to search for this item
 * E.g., via getTargetById('CREATE_ASSET_MENU')
 *
 * This string is not imported/exported so consumers search for this
 * item by duplicating this string.
 * If this is renamed or removed please update all references.
 */
const CreateAssetMenuId = 'CREATE_ASSET_MENU';

export function getCreateAssetMenuItems(
    assets: AssetDescriptor[],
    config: CreateAssetMenuItemsConfig
): FilterableMenuItem {
    return new FilterableMenuItem({
        [FilterableMenuItemArgs.id]: CreateAssetMenuId,
        [FilterableMenuItemArgs.key]: CreateAssetMenuId,
        [FilterableMenuItemArgs.name]: loc(createAssetText),
        [FilterableMenuItemArgs.icon]: CalendarIcons.AddField,
        [FilterableMenuItemArgs.menuContent]: getMenuContent(assets, config),
    });
}

function getMenuContent(
    assetTypes: AssetDescriptor[],
    config: CreateAssetMenuItemsConfig
): IContextualMenuItem[] {
    const enabledAssets = assetTypes.filter(asset => asset.isAssetEnabled?.() ?? true);
    const { displayItems, overflowItems } = splitDisplayedItems(
        enabledAssets,
        config.numInitialItemsToShow
    );

    const items: IContextualMenuItem[] = [];

    displayItems.forEach(descriptor => {
        const menuItem = createMenuItem(descriptor);
        if (menuItem) {
            items.push(menuItem);
        }
    });

    if (config.isShowAllAssetsExpanded) {
        overflowItems.forEach(descriptor => {
            const menuItem = createMenuItem(descriptor);
            if (menuItem) {
                items.push(menuItem);
            }
        });
    }

    if (items.length > 0) {
        items.push(getDivider());
        items.push(
            getShowAllMenuItem(config.isShowAllAssetsExpanded, config.onShowAllAssetsToggled)
        );
    }

    return items;
}

function createMenuItem(assetDescriptor: AssetDescriptor): IContextualMenuItem | undefined {
    return lazyGetTimeStreamAssetMenuItem.tryImportForRender()?.(assetDescriptor);
}

function getDivider() {
    return {
        key: 'CREATE_ASSET_DIVIDER',
        name: '-',
        itemType: ContextualMenuItemType.Divider,
    };
}

function getShowAllMenuItem(
    shouldShowAll: boolean,
    onToggleShowAll: (
        event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
    ) => void
): IContextualMenuItem {
    const buttonText = shouldShowAll ? loc(showLessAssetsText) : loc(showAllAssetsText);

    return {
        key: 'TOGGLE_ASSETS',
        text: buttonText,
        // passing a space instead of an empty string because of a bug in fabric https://github.com/microsoft/fluentui/issues/12425
        icon: ' ', // no icon, but reserve space for alignment purposes
        onClick: onToggleShowAll,
    };
}
